import { Injectable, computed, inject, signal } from '@angular/core';
import { tap, catchError, finalize } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';

import { ObjectsApiService } from '../api/services/objects-api.service';
import { CapyStatsService } from './capystats.service';
import {
  ObjectDto,
  ObjectDetailDto,
  SearchSuggestionsResponseDto,
  ObjectSearchFiltersDto,
  ObjectMapItemDto,
  PaginationMetaDto,
  FiltersDataDto,
  AmenityDto,
  PropertyTypeOption,
  NearbyObjectDto,
  NearbyQueryDto,
} from '../api/models';

/**
 * Store for objects — state management only.
 * Uses public /public/objects endpoints (no auth, no required bounding box).
 */
@Injectable({ providedIn: 'root' })
export class ObjectsStore {
  private readonly api = inject(ObjectsApiService);
  private readonly capyStats = inject(CapyStatsService);

  // ── List state ──
  private readonly _objects = signal<ObjectDto[]>([]);
  private readonly _loading = signal(false);
  private readonly _loadingMore = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _meta = signal<PaginationMetaDto | null>(null);
  private readonly _currentFilters = signal<ObjectSearchFiltersDto>({});

  // ── Detail state ──
  private readonly _selectedObject = signal<ObjectDetailDto | null>(null);
  private readonly _detailLoading = signal(false);
  private readonly _detailError = signal<string | null>(null);

  // ── Suggestions state ──
  private readonly _suggestions = signal<SearchSuggestionsResponseDto | null>(null);
  private readonly _suggestionsLoading = signal(false);

  // ── Map points state (GET /public/objects/map — all offers at once) ──
  private readonly _mapPoints = signal<ObjectMapItemDto[]>([]);
  private readonly _mapPointsLoading = signal(false);
  private mapPointsSubscription: Subscription | null = null;
  private _lastMapPointsFilters: ObjectSearchFiltersDto | null = null;

  // ── Nearby objects state ──
  private readonly _nearbyObjects = signal<NearbyObjectDto[]>([]);
  private readonly _nearbyLoading = signal(false);

  // ── Filters data state ──
  private readonly _filtersData = signal<FiltersDataDto | null>(null);
  private readonly _filtersDataLoading = signal(false);

  // ── Public readonly signals — list ──
  readonly objects = this._objects.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly loadingMore = this._loadingMore.asReadonly();
  readonly error = this._error.asReadonly();
  readonly meta = this._meta.asReadonly();
  readonly currentFilters = this._currentFilters.asReadonly();

  // ── Public readonly signals — detail ──
  readonly selectedObject = this._selectedObject.asReadonly();
  readonly detailLoading = this._detailLoading.asReadonly();
  readonly detailError = this._detailError.asReadonly();

  // ── Public readonly signals — suggestions ──
  readonly suggestions = this._suggestions.asReadonly();
  readonly suggestionsLoading = this._suggestionsLoading.asReadonly();

  // ── Public readonly signals — map points ──
  readonly mapPoints = this._mapPoints.asReadonly();
  readonly mapPointsLoading = this._mapPointsLoading.asReadonly();

  // ── Public readonly signals — nearby ──
  readonly nearbyObjects = this._nearbyObjects.asReadonly();
  readonly nearbyLoading = this._nearbyLoading.asReadonly();

  // ── Public readonly signals — filters data ──
  readonly filtersData = this._filtersData.asReadonly();
  readonly filtersDataLoading = this._filtersDataLoading.asReadonly();

  // ── Computed values — list ──
  readonly count = computed(() => this._objects().length);
  readonly total = computed(() => this._meta()?.total ?? 0);
  readonly totalPages = computed(() => this._meta()?.totalPages ?? 0);
  readonly currentPage = computed(() => this._meta()?.page ?? 1);
  readonly hasNextPage = computed(() => this._meta()?.hasNextPage ?? false);
  readonly hasPreviousPage = computed(() => this._meta()?.hasPreviousPage ?? false);
  readonly isEmpty = computed(() => this._objects().length === 0 && !this._loading());

  // ── Computed values — map points ──
  readonly mapPointsCount = computed(() => this._mapPoints().length);

  // ── Computed values — filters data ──
  readonly availableAmenities = computed((): AmenityDto[] => this._filtersData()?.amenities ?? []);
  readonly availablePropertyTypes = computed(
    (): PropertyTypeOption[] => this._filtersData()?.propertyTypes ?? [],
  );
  readonly priceRange = computed(() => this._filtersData()?.priceRange ?? { min: 0, max: 100000 });
  readonly capacityRange = computed(
    () => this._filtersData()?.capacityRange ?? { min: 1, max: 12 },
  );

  // =========================================================================
  // Filters data
  // =========================================================================

  /**
   * Load filters data (amenities, property types, price/capacity ranges).
   */
  loadFiltersData(): void {
    if (this._filtersData() || this._filtersDataLoading()) return;

    this._filtersDataLoading.set(true);
    this.api
      .getFiltersData()
      .pipe(
        tap((data) => this._filtersData.set(data)),
        catchError((err) => {
          console.error('Failed to load filters data:', err);
          return of(null);
        }),
        finalize(() => this._filtersDataLoading.set(false)),
      )
      .subscribe();
  }

  // =========================================================================
  // Map points (GET /public/objects/map)
  // =========================================================================

  /**
   * Load ALL map points matching the filters in a single request
   * (no bounding box, no pagination — the map renders everything client-side).
   * Cancels any in-flight request so only the latest filters win.
   * Points without coordinates are dropped before storing.
   */
  loadMapPoints(filters: ObjectSearchFiltersDto = {}): void {
    this._lastMapPointsFilters = filters;
    this.mapPointsSubscription?.unsubscribe();
    this._mapPointsLoading.set(true);
    this.mapPointsSubscription = this.api
      .getMapObjects(filters)
      .pipe(
        tap((points) =>
          this._mapPoints.set(points.filter((p) => p.latitude != null && p.longitude != null)),
        ),
        catchError((err) => {
          console.error('Failed to load map points:', err);
          return of(null);
        }),
        finalize(() => this._mapPointsLoading.set(false)),
      )
      .subscribe();
  }

  /**
   * Re-fetch map points after filter changes.
   * Uses the provided filters, or falls back to the last ones used.
   */
  reloadMapPoints(filters?: ObjectSearchFiltersDto): void {
    this.loadMapPoints(filters ?? this._lastMapPointsFilters ?? {});
  }

  // =========================================================================
  // Nearby objects
  // =========================================================================

  /**
   * Load objects near a specific point, sorted by distance.
   * GET /public/objects/nearby
   */
  loadNearbyObjects(query: NearbyQueryDto): void {
    this._nearbyLoading.set(true);
    this.api
      .getNearby(query)
      .pipe(
        tap((data) => this._nearbyObjects.set(data)),
        catchError((err) => {
          console.error('Failed to load nearby objects:', err);
          this._nearbyObjects.set([]);
          return of(null);
        }),
        finalize(() => this._nearbyLoading.set(false)),
      )
      .subscribe();
  }

  // =========================================================================
  // List operations
  // =========================================================================

  /**
   * Load objects with filters (bounding box is optional).
   */
  loadObjects(filters: ObjectSearchFiltersDto = {}): void {
    // Only show loading spinner if no data loaded yet (avoids flash on back navigation)
    if (this._objects().length === 0) {
      this._loading.set(true);
    }
    this._error.set(null);
    this._currentFilters.set(filters);

    this.api
      .search(filters)
      .pipe(
        tap((response) => {
          this._objects.set(response.data);
          this._meta.set(response.meta);

          const resultCount = response.meta?.total ?? response.data.length;
          this.capyStats.track('SearchExecuted', {
            resultCount,
            returnedCount: response.data.length,
            zeroResults: resultCount === 0,
            hasQuery: !!filters.search,
            ...(filters.search ? { query: filters.search } : {}),
            ...(filters.city ? { city: filters.city } : {}),
            ...(filters.minCapacity ? { minCapacity: filters.minCapacity } : {}),
            ...(filters.minPrice ? { minPrice: filters.minPrice } : {}),
            ...(filters.maxPrice ? { maxPrice: filters.maxPrice } : {}),
            ...(filters.petPolicy ? { petPolicy: filters.petPolicy } : {}),
            ...(filters.minStay ? { minStay: filters.minStay } : {}),
            amenityCount: filters.amenityIds?.length ?? 0,
            propertyTypeCount: filters.propertyTypeIds?.length ?? 0,
            ...(filters.sortBy ? { sortBy: filters.sortBy } : {}),
            ...(filters.sortOrder ? { sortOrder: filters.sortOrder } : {}),
            hasDates: !!filters.dateFrom,
          });
        }),
        catchError((err) => {
          console.error('Failed to load objects:', err);
          this._error.set(err?.error?.message || err.message || 'Nie udalo sie pobrac obiektow');
          this._objects.set([]);
          this._meta.set(null);
          return of(null);
        }),
        finalize(() => this._loading.set(false)),
      )
      .subscribe();
  }

  /**
   * Load next page (appending to existing list).
   */
  loadNextPage(): void {
    const meta = this._meta();
    if (!meta?.hasNextPage || this._loading() || this._loadingMore()) return;

    const filters = this._currentFilters();
    const nextPage = meta.page + 1;

    this._loadingMore.set(true);
    this.api
      .search({ ...filters, page: nextPage })
      .pipe(
        tap((response) => {
          this._objects.update((prev) => [...prev, ...response.data]);
          this._meta.set(response.meta);
        }),
        catchError((err) => {
          console.error('Failed to load next page:', err);
          return of(null);
        }),
        finalize(() => this._loadingMore.set(false)),
      )
      .subscribe();
  }

  // =========================================================================
  // Detail operations
  // =========================================================================

  /**
   * Load full object details by slug (public endpoint).
   */
  loadObjectBySlug(slug: string): void {
    this._detailLoading.set(true);
    this._detailError.set(null);
    this._selectedObject.set(null);

    this.api
      .getBySlug(slug)
      .pipe(
        tap((detail) => this._selectedObject.set(detail)),
        catchError((err) => {
          console.error('Failed to load object detail:', err);
          this._detailError.set(
            err?.error?.message || err.message || 'Nie udalo sie pobrac obiektu',
          );
          return of(null);
        }),
        finalize(() => this._detailLoading.set(false)),
      )
      .subscribe();
  }

  /**
   * Clear selected object detail.
   */
  clearSelectedObject(): void {
    this._selectedObject.set(null);
    this._detailError.set(null);
  }

  // =========================================================================
  // Suggestions / autocomplete
  // =========================================================================

  /**
   * Load autocomplete suggestions (min 2 chars).
   */
  loadSuggestions(search: string): void {
    if (search.length < 2) {
      this._suggestions.set(null);
      return;
    }

    this._suggestionsLoading.set(true);
    this.api
      .suggestions(search)
      .pipe(
        tap((suggestions) => this._suggestions.set(suggestions)),
        catchError(() => {
          this._suggestions.set(null);
          return of(null);
        }),
        finalize(() => this._suggestionsLoading.set(false)),
      )
      .subscribe();
  }

  /**
   * Clear suggestions.
   */
  clearSuggestions(): void {
    this._suggestions.set(null);
  }

  // =========================================================================
  // Utility
  // =========================================================================

  /**
   * Get object by ID from current list (without API call).
   */
  getObjectById(id: number): ObjectDto | undefined {
    return this._objects().find((obj) => obj.id === id);
  }

  /**
   * Clear all list state.
   */
  clearObjects(): void {
    this._objects.set([]);
    this._meta.set(null);
    this._error.set(null);
    this._currentFilters.set({});
  }
}
